import React, { useEffect, useRef, useState } from 'react';
import { listShares, addShare, removeShare, setProjectVisibility } from '../data/projects';
import './ShareDialog.css';

const ROLE_LABEL = { editor: 'עורך', viewer: 'צופה' };

function initialsOf(name, email) {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return parts[0][0] + parts[1][0];
  return src.slice(0, 2).toUpperCase();
}

export default function ShareDialog({ project, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visibility, setVisibility] = useState('firm');
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [savingVis, setSavingVis] = useState(false);

  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const reload = () =>
    listShares(project.id)
      .then((d) => { setVisibility(d.visibility || 'firm'); setShares(d.shares || []); setError(''); })
      .catch((e) => setError(e.message || 'טעינת השיתופים נכשלה.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc to close + focus the email field.
  useEffect(() => {
    const node = modalRef.current;
    const prev = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } };
    node.addEventListener('keydown', onKey);
    node.querySelector('.shr-invite input')?.focus();
    return () => { node.removeEventListener('keydown', onKey); if (prev?.focus) prev.focus(); };
  }, []);

  async function changeVisibility(next) {
    if (next === visibility || savingVis) return;
    setVisibility(next);
    setSavingVis(true);
    try { await setProjectVisibility(project.id, next); setError(''); }
    catch (e) { setError(e.message || 'עדכון הנראות נכשל.'); }
    finally { setSavingVis(false); }
  }

  async function invite(e) {
    e.preventDefault();
    if (!email.trim() || inviting) return;
    setInviting(true);
    try {
      await addShare(project.id, email.trim(), role);
      setEmail('');
      await reload();
      setError('');
    } catch (err) {
      setError(err.message || 'השיתוף נכשל.');
    } finally {
      setInviting(false);
    }
  }

  async function remove(shareId) {
    await removeShare(project.id, shareId).catch(() => {});
    reload();
  }

  return (
    <div className="shr-backdrop" onMouseDown={onClose}>
      <div ref={modalRef} className="shr-modal" role="dialog" aria-modal="true" aria-label="שיתוף פרויקט" tabIndex={-1} onMouseDown={(e) => e.stopPropagation()}>
        <header className="shr-head">
          <h2>שיתוף הפרויקט</h2>
          <button type="button" className="shr-x" onClick={onClose} aria-label="סגור"><i className="fi fi-rr-cross-small" /></button>
        </header>
        <p className="shr-name" title={project.name}>{project.name}</p>

        <div className="shr-body">
          {error && <div className="shr-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

          {/* Visibility */}
          <div className="shr-section-title">מי יכול לגשת</div>
          <div className="shr-vis">
            <button type="button" className={`shr-vis-opt ${visibility === 'firm' ? 'is-active' : ''}`} onClick={() => changeVisibility('firm')}>
              <i className="fi fi-rr-users" />
              <div>
                <span className="shr-vis-label">כל המשרד</span>
                <span className="shr-vis-sub">כל חברי המשרד יכולים לצפות ולערוך</span>
              </div>
              {visibility === 'firm' && <i className="fi fi-rr-check shr-vis-check" />}
            </button>
            <button type="button" className={`shr-vis-opt ${visibility === 'private' ? 'is-active' : ''}`} onClick={() => changeVisibility('private')}>
              <i className="fi fi-rr-lock" />
              <div>
                <span className="shr-vis-label">מוגבל</span>
                <span className="shr-vis-sub">רק אתם והאנשים שתזמינו</span>
              </div>
              {visibility === 'private' && <i className="fi fi-rr-check shr-vis-check" />}
            </button>
          </div>

          {/* Invite */}
          <div className="shr-section-title">הזמנת אנשים</div>
          <form className="shr-invite" onSubmit={invite}>
            <input className="shr-ltr" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firm.co.il" aria-label="אימייל" />
            <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="הרשאה">
              <option value="viewer">צופה</option>
              <option value="editor">עורך</option>
            </select>
            <button type="submit" className="shr-invite-btn" disabled={inviting || !email.trim()}>
              {inviting ? '…' : 'הזמנה'}
            </button>
          </form>

          {/* People with explicit access */}
          {loading ? (
            <div className="shr-loading"><span className="rp-spinner" /> טוען…</div>
          ) : shares.length === 0 ? (
            <p className="shr-empty">לא הוזמנו אנשים נוספים מעבר {visibility === 'firm' ? 'לחברי המשרד' : 'לכם'}.</p>
          ) : (
            <ul className="shr-list">
              {shares.map((s) => (
                <li key={s.id} className="shr-row">
                  <span className="shr-avatar">{initialsOf(s.name, s.email)}</span>
                  <div className="shr-id">
                    <span className="shr-row-name">{s.name || s.email}</span>
                    {s.name && <span className="shr-row-email shr-ltr">{s.email}</span>}
                  </div>
                  {s.status === 'pending' && <span className="shr-pending">ממתין/ה להרשמה</span>}
                  <span className="shr-role">{ROLE_LABEL[s.role] || s.role}</span>
                  <button type="button" className="shr-remove" aria-label="הסרה" onClick={() => remove(s.id)}><i className="fi fi-rr-cross-small" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="shr-foot">
          <button type="button" className="shr-done" onClick={onClose}>סיום</button>
        </footer>
      </div>
    </div>
  );
}
